import React, { useLayoutEffect, useRef, useState } from 'react';
import type { DashboardAnnualDaySlot } from '../../utils/dashboardAnnualDaySlots';
import { annualSlotHasTimetableProgramAssignment } from '../../utils/annualProgressSlotSource';
import type { TodoNoteCategoryDraft, TodoNoteItemView } from '../../types/todoNote';
import {
  DASHBOARD_WEEKDAY_EN,
  formatDashboardWeekMmDd,
  weekDateIsosFromMonday,
} from '../../utils/dashboardSemesterWeek';
import { toISODate } from '../../utils/date';
import DashboardWeeklyLessonActionMenu from './DashboardWeeklyLessonActionMenu';
import { DashboardLessonEvalStatusBadge } from './DashboardLessonEvalStatusBadge';
import TodoChecklistText from '../todoNote/TodoChecklistText';

export type DashboardWeeklyTodoRow = {
  item: TodoNoteItemView;
  category: TodoNoteCategoryDraft | null;
};

type PeriodTime = { start: string; end: string };

export function weeklyBehaviorRecordKey(dateIso: string, slotIndex: number): string {
  return `${dateIso}:${slotIndex}`;
}

type Props = {
  mondayIso: string;
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso?: Readonly<Record<string, string>>;
  /** 날짜 ISO → 그날 수업 슬롯 (일요는 보통 빈 배열) */
  slotsByDateIso: Readonly<Record<string, DashboardAnnualDaySlot[]>>;
  /** 날짜 ISO → 그날 마감 TODO */
  todosByDateIso: Readonly<Record<string, DashboardWeeklyTodoRow[]>>;
  /** 행동기록 존재 키 (`dateIso:slotIndex`) */
  behaviorRecordKeySet?: ReadonlySet<string>;
  /** 교시 시각 (`dateIso:slotIndex`) */
  periodTimeByKey?: Readonly<Record<string, PeriodTime>>;
  /** 요일 블럭 세로 나열 (수동 on 또는 좁은 화면 자동) */
  daysVertical?: boolean;
  /** 해당 날짜를 마감일로 TODO 추가 모달 열기 */
  onAddTodo?: (dateIso: string) => void;
  /** TODO 블럭 클릭 → 수정 모달 */
  onOpenTodo?: (item: TodoNoteItemView) => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  onOpenProgram?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
};

function holidayNameFor(
  iso: string | undefined,
  holidayIsoSet: ReadonlySet<string>,
  holidayNoteByIso: Readonly<Record<string, string>> | undefined
): string {
  if (!iso || !holidayIsoSet.has(iso)) return '';
  return (holidayNoteByIso?.[iso] ?? '').trim();
}

function isSlotNoLesson(slot: DashboardAnnualDaySlot): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return true;
  return !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex);
}

function WeeklyMiniLessonSlot({
  slot,
  periodTime,
  hasBehaviorRecord,
  menuOpen,
  onToggleMenu,
  onOpenPresentation,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
}: {
  slot: DashboardAnnualDaySlot;
  periodTime?: PeriodTime;
  hasBehaviorRecord: boolean;
  menuOpen: boolean;
  onToggleMenu: (slot: DashboardAnnualDaySlot, anchorEl: HTMLElement) => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
}) {
  const d = slot.display;
  const unit = (d.progressUnit ?? '').trim();
  const learn = [(d.progressLearning ?? '').trim(), (d.progressLearningNote ?? '').trim()]
    .filter(Boolean)
    .join(' ');
  const periodLabel = (slot.periodLabel ?? '').trim();
  const pyeonje = (slot.templateLabel ?? '').trim();
  const hex = (slot.timetableColorHex ?? '').trim();
  const hasTimetableBg = Boolean(hex);
  const isNonClassStripeSlot = slot.timetableSlotKind === 'non_class' && !hasTimetableBg;
  const summaryText = [[periodLabel, pyeonje, unit].filter(Boolean).join(' '), learn]
    .filter(Boolean)
    .join('\n');
  const tintBg = hasTimetableBg ? (
    <div
      className={
        'cp-dash-weekly-mini-slot-bg' +
        (slot.programKindLabel === '프로' ? ' is-project' : '') +
        (slot.timetableSlotKind === 'non_class' ? ' is-non-class' : '')
      }
      style={{ backgroundColor: hex }}
      aria-hidden
    />
  ) : null;
  const hasLessonRecord = d.progressClassRecordKind !== 'empty';
  const classRecKind = d.progressClassRecordKind;
  const classRecChipClass =
    classRecKind === 'eval_log'
      ? 'is-eval_log'
      : classRecKind === 'custom'
        ? 'is-custom'
        : 'is-lesson';
  const classRecChipLabel =
    classRecKind === 'eval_log' ? '평' : classRecKind === 'custom' ? '기' : '수';
  const classRecChipTitle =
    classRecKind === 'eval_log'
      ? '평가기록'
      : classRecKind === 'custom'
        ? '기록'
        : '수업기록';
  const interactive = Boolean(onOpenPresentation || onOpenLessonRecord || onOpenBehaviorRecord);
  const rootRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      ref={rootRef}
      className={[
        'cp-dash-weekly-mini-slot',
        'is-lesson',
        isNonClassStripeSlot ? 'is-nonclass' : '',
        hasTimetableBg ? 'has-timetable-bg' : '',
        interactive ? 'is-interactive' : '',
        menuOpen ? 'is-menu-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-tutorial-dash="weekly-slot"
      style={
        hasTimetableBg && /^#[0-9A-Fa-f]{6}$/.test(hex)
          ? ({ '--cp-dash-slot-color': hex } as React.CSSProperties)
          : undefined
      }
      title={summaryText}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={
        interactive
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              const el = rootRef.current;
              if (el) onToggleMenu(slot, el);
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                const el = rootRef.current;
                if (el) onToggleMenu(slot, el);
              }
            }
          : undefined
      }
    >
      <div className="cp-dash-weekly-mini-slot-rail">
        {tintBg}
        <span className="cp-dash-weekly-mini-slot-period">{periodLabel || '—'}</span>
        {periodTime?.start ? (
          <span className="cp-dash-weekly-mini-slot-time">{periodTime.start}</span>
        ) : null}
        {periodTime?.end ? (
          <span className="cp-dash-weekly-mini-slot-time">{periodTime.end}</span>
        ) : null}
      </div>
      <div className="cp-dash-weekly-mini-slot-main">
        {tintBg}
        {(hasLessonRecord || hasBehaviorRecord) ? (
          <div className="cp-dash-weekly-mini-slot-chips" aria-label="기록 표시">
            {hasLessonRecord ? (
              <span
                className={`cp-dash-weekly-mini-slot-chip ${classRecChipClass}`}
                title={classRecChipTitle}
              >
                {classRecChipLabel}
              </span>
            ) : null}
            {hasBehaviorRecord ? (
              <span className="cp-dash-weekly-mini-slot-chip is-behavior" title="행동기록">
                행
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="cp-dash-weekly-mini-slot-body">
          <span className="cp-dash-weekly-mini-slot-head">
            {pyeonje ? <span className="cp-dash-weekly-mini-slot-pyeonje">{pyeonje}</span> : null}
            {unit ? <span className="cp-dash-weekly-mini-slot-unit">{unit}</span> : null}
            {!pyeonje && !unit ? <span className="cp-dash-weekly-mini-slot-unit">—</span> : null}
            <DashboardLessonEvalStatusBadge code={d.progressEvalCode} label={d.progressEval} />
          </span>
          <span className="cp-dash-weekly-mini-slot-learn">{learn || '\u00a0'}</span>
        </div>
      </div>
    </div>
  );
}

function WeeklyDayContent({
  dateIso,
  weekdayEn,
  holidayName,
  slots,
  todos,
  behaviorRecordKeySet,
  periodTimeByKey,
  menuSlotKey,
  onToggleMenu,
  onAddTodo,
  onOpenTodo,
  onOpenPresentation,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
}: {
  dateIso: string;
  weekdayEn: string;
  holidayName: string;
  slots: DashboardAnnualDaySlot[];
  todos: DashboardWeeklyTodoRow[];
  behaviorRecordKeySet?: ReadonlySet<string>;
  periodTimeByKey?: Readonly<Record<string, PeriodTime>>;
  menuSlotKey: string | null;
  onToggleMenu: (slot: DashboardAnnualDaySlot, anchorEl: HTMLElement) => void;
  onAddTodo?: (dateIso: string) => void;
  onOpenTodo?: (item: TodoNoteItemView) => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
}) {
  const lessonSlots = slots.filter((s) => !isSlotNoLesson(s)).slice(0, 10);
  const lessonRows = lessonSlots.length >= 9 ? 5 : 4;
  const headRef = useRef<HTMLElement | null>(null);
  const todosRef = useRef<HTMLDivElement | null>(null);

  /** 스크롤 상단=요일 테두리, 첫 TODO 상단=첫 수업 상단 → head 높이만큼 스크롤 내부 패딩 */
  useLayoutEffect(() => {
    const head = headRef.current;
    const todos = todosRef.current;
    if (!head || !todos) return;
    const apply = () => {
      todos.style.setProperty('--cp-dash-week-todo-lead', `${head.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(head);
    return () => ro.disconnect();
  }, [dateIso, weekdayEn, holidayName]);

  const isToday = Boolean(dateIso) && dateIso === toISODate(new Date());

  return (
    <div className="cp-dash-weekly-day-content">
      <div className="cp-dash-weekly-day-main">
        <header ref={headRef} className="cp-dash-weekly-day-head">
          <span
            className={'cp-dash-weekly-day-date' + (isToday ? ' is-today' : '')}
            aria-current={isToday ? 'date' : undefined}
          >
            {formatDashboardWeekMmDd(dateIso)}
          </span>
          <span className="cp-dash-weekly-day-en">{weekdayEn}</span>
          {holidayName ? (
            <span className="cp-dash-weekly-day-holiday-name">{holidayName}</span>
          ) : null}
        </header>
        <div
          className={
            'cp-dash-weekly-day-lessons' + (lessonRows === 5 ? ' is-rows-5' : ' is-rows-4')
          }
          aria-label={`${dateIso} 수업`}
        >
          {lessonSlots.length === 0 ? (
            <p className="cp-dash-weekly-day-lessons-empty">수업 없음</p>
          ) : (
            lessonSlots.map((slot, idx) => {
              const key = `${slot.dateIso}:${slot.slotIndex}:${slot.annualCellKey}-${idx}`;
              const menuKey = weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
              return (
                <WeeklyMiniLessonSlot
                  key={key}
                  slot={slot}
                  periodTime={periodTimeByKey?.[menuKey]}
                  hasBehaviorRecord={!!behaviorRecordKeySet?.has(menuKey)}
                  menuOpen={menuSlotKey === menuKey}
                  onToggleMenu={onToggleMenu}
                  onOpenPresentation={onOpenPresentation}
                  onOpenLessonRecord={onOpenLessonRecord}
                  onOpenBehaviorRecord={onOpenBehaviorRecord}
                />
              );
            })
          )}
        </div>
      </div>
      <div
        ref={todosRef}
        className={
          'cp-dash-weekly-day-todos' + (lessonRows === 5 ? ' is-rows-5' : ' is-rows-4')
        }
        aria-label={`${dateIso} TODO`}
        data-tutorial-dash="weekly-todos"
      >
        <DashboardWeeklyTodoItems
          dateIso={dateIso}
          todos={todos}
          onAddTodo={onAddTodo}
          onOpenTodo={onOpenTodo}
        />
      </div>
    </div>
  );
}

const TODO_POSTIT_DEFAULT_CAT = '#e7c45a';

function parseHexRgb(raw: string): [number, number, number] | null {
  const m = raw.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** 데일리 포스트잇 종이색: (카테고리 42% + #c8c8c8) 26% + 흰색 */
function todoPostitPaperHex(catColor: string): string | null {
  const rgb = parseHexRgb(catColor);
  if (!rgb) return null;
  const paper = rgb.map((c) => {
    const tinted = c * 0.42 + 200 * 0.58;
    return Math.round(tinted * 0.26 + 255 * 0.74);
  });
  return `#${paper.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** 날짜별 마감 TODO 포스트잇 + 추가 버튼 (위클리·시간표 모드 공용) */
export function DashboardWeeklyTodoItems({
  dateIso,
  todos,
  onAddTodo,
  onOpenTodo,
  addPosition = 'end',
}: {
  dateIso: string;
  todos: readonly DashboardWeeklyTodoRow[];
  onAddTodo?: (dateIso: string) => void;
  onOpenTodo?: (item: TodoNoteItemView) => void;
  addPosition?: 'start' | 'end';
}) {
  const addButton =
    dateIso && onAddTodo ? (
      <button
        type="button"
        className="cp-dash-weekly-todo-add"
        onClick={() => onAddTodo(dateIso)}
        aria-label={`${formatDashboardWeekMmDd(dateIso)} TODO 추가`}
        title="TODO 추가"
      >
        +
      </button>
    ) : null;
  return (
    <>
      {addPosition === 'start' ? addButton : null}
      {todos.map(({ item, category }) => {
        const title = (item.title ?? '').trim();
        const body = (item.body ?? '').trim();
        const firstLine = title || body.split('\n')[0]?.trim() || '(내용 없음)';
        const restLines = title
          ? body
          : body.includes('\n')
            ? body.split('\n').slice(1).join('\n').trim()
            : '';
        const catColor = (category?.color ?? '').trim();
        const hasCatColor = /^#[0-9A-Fa-f]{6}$/.test(catColor);
        const isCompleted = !!item.completed;
        const canOpen = Boolean(onOpenTodo);
        return (
          <button
            key={item.id}
            type="button"
            className={
              'cp-dash-weekly-todo' +
              (isCompleted ? ' is-completed' : '') +
              (item.is_routine || item.routineParentId ? ' is-routine' : '') +
              (canOpen ? ' is-interactive' : '')
            }
            style={
              {
                '--todo-postit-cat': catColor || TODO_POSTIT_DEFAULT_CAT,
                '--todo-postit-paper-hex':
                  todoPostitPaperHex(catColor || TODO_POSTIT_DEFAULT_CAT) ?? undefined,
              } as React.CSSProperties
            }
            onClick={() => onOpenTodo?.(item)}
            disabled={!canOpen}
            aria-label={isCompleted ? `완료 TODO 수정: ${firstLine}` : `TODO 수정: ${firstLine}`}
          >
            {category?.name ? (
              <span
                className={
                  'cp-dash-weekly-todo-cat' + (hasCatColor && !isCompleted ? ' has-color' : '')
                }
                style={
                  hasCatColor && !isCompleted
                    ? ({ borderColor: catColor } as React.CSSProperties)
                    : undefined
                }
              >
                {category.name}
              </span>
            ) : null}
            <div className="cp-dash-weekly-todo-memo">
              <div className="cp-dash-weekly-todo-memo-first">
                <TodoChecklistText text={firstLine} />
              </div>
              {restLines ? (
                <div className="cp-dash-weekly-todo-memo-rest">
                  <TodoChecklistText text={restLines} />
                </div>
              ) : null}
            </div>
          </button>
        );
      })}
      {addPosition === 'end' ? addButton : null}
    </>
  );
}

/**
 * 대시보드 위클리: 월~금 블럭 + 토·일 통합 칸.
 * 왼쪽 수업(2열×4행, 9~10개면 5행 · 1열 위→아래 후 2열) · 오른쪽 마감 TODO.
 */
const DashboardWeeklyPlanner: React.FC<Props> = ({
  mondayIso,
  holidayIsoSet,
  holidayNoteByIso,
  slotsByDateIso,
  todosByDateIso,
  behaviorRecordKeySet,
  periodTimeByKey,
  daysVertical = false,
  onAddTodo,
  onOpenTodo,
  onOpenPresentation,
  onOpenProgram,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
}) => {
  const dates = weekDateIsosFromMonday(mondayIso);
  const [menu, setMenu] = useState<{
    slot: DashboardAnnualDaySlot;
    rect: DOMRect;
  } | null>(null);

  const weekdayBlocks = [
    { iso: dates[0], en: DASHBOARD_WEEKDAY_EN[0] },
    { iso: dates[1], en: DASHBOARD_WEEKDAY_EN[1] },
    { iso: dates[2], en: DASHBOARD_WEEKDAY_EN[2] },
    { iso: dates[3], en: DASHBOARD_WEEKDAY_EN[3] },
    { iso: dates[4], en: DASHBOARD_WEEKDAY_EN[4] },
  ];

  const menuSlotKey = menu
    ? weeklyBehaviorRecordKey(menu.slot.dateIso, menu.slot.slotIndex)
    : null;

  const onToggleMenu = (slot: DashboardAnnualDaySlot, anchorEl: HTMLElement) => {
    const key = weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
    setMenu((prev) => {
      if (prev && weeklyBehaviorRecordKey(prev.slot.dateIso, prev.slot.slotIndex) === key) {
        return null;
      }
      return { slot, rect: anchorEl.getBoundingClientRect() };
    });
  };

  return (
    <div className="cp-dash-weekly" aria-label="위클리 주간 보기">
      <div
        className={'cp-dash-weekly-grid' + (daysVertical ? ' is-days-vertical' : '')}
      >
        {weekdayBlocks.map((b) => {
          const isHoliday = !!b.iso && holidayIsoSet.has(b.iso);
          const holidayName = holidayNameFor(b.iso, holidayIsoSet, holidayNoteByIso);
          return (
            <article
              key={b.en}
              className={'cp-dash-weekly-day' + (isHoliday ? ' is-holiday' : '')}
            >
              <WeeklyDayContent
                dateIso={b.iso || ''}
                weekdayEn={b.en}
                holidayName={holidayName}
                slots={b.iso ? slotsByDateIso[b.iso] ?? [] : []}
                todos={b.iso ? todosByDateIso[b.iso] ?? [] : []}
                behaviorRecordKeySet={behaviorRecordKeySet}
                periodTimeByKey={periodTimeByKey}
                menuSlotKey={menuSlotKey}
                onToggleMenu={onToggleMenu}
                onAddTodo={onAddTodo}
                onOpenTodo={onOpenTodo}
                onOpenPresentation={onOpenPresentation}
                onOpenLessonRecord={onOpenLessonRecord}
                onOpenBehaviorRecord={onOpenBehaviorRecord}
              />
            </article>
          );
        })}

        <article className="cp-dash-weekly-day cp-dash-weekly-day--weekend">
          {[5, 6].map((idx) => {
            const iso = dates[idx];
            const isHoliday = !!iso && holidayIsoSet.has(iso);
            const holidayName = holidayNameFor(iso, holidayIsoSet, holidayNoteByIso);
            return (
              <div
                key={DASHBOARD_WEEKDAY_EN[idx]}
                className={'cp-dash-weekly-weekend-half' + (isHoliday ? ' is-holiday' : '')}
              >
                <WeeklyDayContent
                  dateIso={iso || ''}
                  weekdayEn={DASHBOARD_WEEKDAY_EN[idx]}
                  holidayName={holidayName}
                  slots={iso ? slotsByDateIso[iso] ?? [] : []}
                  todos={iso ? todosByDateIso[iso] ?? [] : []}
                  behaviorRecordKeySet={behaviorRecordKeySet}
                  periodTimeByKey={periodTimeByKey}
                  menuSlotKey={menuSlotKey}
                  onToggleMenu={onToggleMenu}
                  onAddTodo={onAddTodo}
                  onOpenTodo={onOpenTodo}
                  onOpenPresentation={onOpenPresentation}
                  onOpenLessonRecord={onOpenLessonRecord}
                  onOpenBehaviorRecord={onOpenBehaviorRecord}
                />
              </div>
            );
          })}
        </article>
      </div>
      {menu ? (
        <DashboardWeeklyLessonActionMenu
          slot={menu.slot}
          anchorRect={menu.rect}
          onClose={() => setMenu(null)}
          onOpenPresentation={onOpenPresentation}
          onOpenProgram={onOpenProgram}
          onOpenLessonRecord={onOpenLessonRecord}
          onOpenBehaviorRecord={onOpenBehaviorRecord}
        />
      ) : null}
    </div>
  );
};

export default DashboardWeeklyPlanner;
